import React, { useState } from 'react';
import axios from 'axios';
import { format, addDays, addMonths, addYears } from 'date-fns';
import { Calendar, X, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * ValidityEditorModal — single-user expiry editor for User Management.
 *
 * Two states:
 *   1. Quick presets (+7d / +30d / +90d / +1y / Lifetime) — most common
 *      admin action, one tap and done.
 *   2. Custom date input for exact expiry.
 *
 * Saves via PUT /api/admin/users/{user_id}/validity. Calls `onSaved()` so
 * the parent table can refresh in place.
 */
const ValidityEditorModal = ({ user, onClose, onSaved }) => {
  const [customDate, setCustomDate] = useState(
    user?.valid_until ? format(new Date(user.valid_until), 'yyyy-MM-dd') : '',
  );
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const presets = [
    { label: '+7 days',    fn: () => addDays(new Date(), 7) },
    { label: '+30 days',   fn: () => addDays(new Date(), 30) },
    { label: '+90 days',   fn: () => addMonths(new Date(), 3) },
    { label: '+1 year',    fn: () => addYears(new Date(), 1) },
  ];

  const apply = async ({ valid_until = null, clear = false }) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/users/${user.id}/validity`,
        clear ? { clear: true } : { valid_until },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast({
        title: clear ? 'Lifetime access granted' : 'Validity updated',
        description: clear
          ? `${user.name} no longer has an expiry.`
          : `${user.name} can read until ${format(new Date(valid_until), 'MMM d, yyyy')}.`,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      toast({
        title: 'Failed to update validity',
        description: err?.response?.data?.detail || err?.message || 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreset = (preset) => {
    const d = preset.fn();
    apply({ valid_until: d.toISOString() });
  };

  const handleCustom = (e) => {
    e.preventDefault();
    if (!customDate) {
      toast({ title: 'Pick a date first', variant: 'destructive' });
      return;
    }
    // Use end-of-day so the user retains access throughout the chosen date.
    const dt = new Date(`${customDate}T23:59:59`);
    if (Number.isNaN(dt.getTime())) {
      toast({ title: 'Invalid date', variant: 'destructive' });
      return;
    }
    apply({ valid_until: dt.toISOString() });
  };

  const currentlyExpired = user?.valid_until && new Date(user.valid_until) < new Date();

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="validity-editor-modal"
    >
      <div className="bg-zinc-950 border border-white/10 rounded-2xl max-w-md w-full p-6 text-white shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Calendar className="h-5 w-5 text-emerald-400" /> Account validity
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {user?.name} <span className="text-gray-500">·</span> {user?.email}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md hover:bg-white/5 text-gray-400 hover:text-white"
            aria-label="Close"
            data-testid="validity-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Current state */}
        <div className={`mb-5 rounded-lg p-3 text-sm ${
          currentlyExpired
            ? 'bg-red-500/10 border border-red-500/30 text-red-200'
            : user?.valid_until
            ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-200'
            : 'bg-white/5 border border-white/10 text-gray-300'
        }`}>
          {currentlyExpired && <AlertTriangle className="inline h-4 w-4 mr-1.5 -mt-0.5" />}
          {user?.valid_until ? (
            <>
              <span className="font-semibold">{currentlyExpired ? 'Expired on ' : 'Valid until '}</span>
              {format(new Date(user.valid_until), 'MMMM d, yyyy')}
            </>
          ) : (
            <span className="font-semibold">Lifetime access (no expiry set)</span>
          )}
        </div>

        {/* Quick presets */}
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Quick extend</p>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => (
              <button
                key={p.label}
                type="button"
                disabled={saving}
                onClick={() => handlePreset(p)}
                className="text-xs px-3 py-1.5 rounded-md bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-200 disabled:opacity-50"
                data-testid={`validity-preset-${p.label.replace(/\s+/g, '-').replace('+', 'plus')}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom date */}
        <form onSubmit={handleCustom} className="mb-4">
          <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Pick exact date</p>
          <div className="flex gap-2">
            <Input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              min={format(new Date(), 'yyyy-MM-dd')}
              className="flex-1 bg-zinc-900 border-white/10 text-white"
              data-testid="validity-custom-date"
            />
            <Button
              type="submit"
              disabled={saving || !customDate}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="validity-custom-apply"
            >
              Apply
            </Button>
          </div>
        </form>

        {/* Clear / lifetime */}
        <div className="pt-4 border-t border-white/10 flex items-center justify-between">
          <span className="text-xs text-gray-400">Grant lifetime access:</span>
          <button
            type="button"
            disabled={saving || !user?.valid_until}
            onClick={() => apply({ clear: true })}
            className="text-xs px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-gray-200 disabled:opacity-40"
            data-testid="validity-clear"
          >
            Clear expiry
          </button>
        </div>
      </div>
    </div>
  );
};

export default ValidityEditorModal;
